import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import s from './AskiAnalysis.module.css';

const API = process.env.REACT_APP_API_BASE_URL || '';

const fmtPeriod = (d) => {
  const str = String(d);
  if (str.length === 6) return `${str.slice(4)}/${str.slice(0, 4)}`;
  return str;
};

const fmtNum = (n) => {
  if (n === null || n === undefined) return '-';
  return parseFloat(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
};

const AskiAnalysis = () => {
  const [meta, setMeta] = useState({ periods: [], ilceler: [], aboneler: [] });
  const [metaLoading, setMetaLoading] = useState(true);

  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [ilce, setIlce] = useState('');
  const [aboneno, setAboneno] = useState('');
  const [groupBy, setGroupBy] = useState('period');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState('analysis');
  const [chartType, setChartType] = useState('bar');

  const [cmpP1Start, setCmpP1Start] = useState('');
  const [cmpP1End, setCmpP1End] = useState('');
  const [cmpP2Start, setCmpP2Start] = useState('');
  const [cmpP2End, setCmpP2End] = useState('');
  const [cmpData, setCmpData] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const pdfRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setMetaLoading(true);
        const res = await fetch(`${API}/api/aski/analysis/periods`);
        const d = await res.json();
        setMeta(d);
        if (d.periods.length > 0) {
          setStartPeriod(d.periods[d.periods.length - 1]);
          setEndPeriod(d.periods[0]);
          setCmpP1Start(d.periods[Math.min(d.periods.length - 1, 11)]);
          setCmpP1End(d.periods[Math.min(d.periods.length - 1, 6)]);
          setCmpP2Start(d.periods[Math.min(d.periods.length - 1, 5)]);
          setCmpP2End(d.periods[0]);
        }
      } catch (e) { console.error('Meta yuklenemedi:', e); }
      finally { setMetaLoading(false); }
    })();
  }, []);

  const runQuery = useCallback(async () => {
    if (!startPeriod || !endPeriod) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/aski/analysis/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startPeriod, endPeriod, ilce: ilce || undefined, aboneno: aboneno || undefined, groupBy }),
      });
      const d = await res.json();
      setData(d);
    } catch (e) { console.error('Sorgu hatasi:', e); }
    finally { setLoading(false); }
  }, [startPeriod, endPeriod, ilce, aboneno, groupBy]);

  const runCompare = useCallback(async () => {
    if (!cmpP1Start || !cmpP1End || !cmpP2Start || !cmpP2End) return;
    try {
      setCmpLoading(true);
      const res = await fetch(`${API}/api/aski/analysis/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period1Start: cmpP1Start, period1End: cmpP1End, period2Start: cmpP2Start, period2End: cmpP2End, ilce: ilce || undefined, aboneno: aboneno || undefined }),
      });
      const d = await res.json();
      setCmpData(d);
    } catch (e) { console.error('Karsilastirma hatasi:', e); }
    finally { setCmpLoading(false); }
  }, [cmpP1Start, cmpP1End, cmpP2Start, cmpP2End, ilce, aboneno]);

  const exportPDF = async () => {
    if (!pdfRef.current) return;
    try {
      setPdfExporting(true);
      await new Promise(r => setTimeout(r, 300));
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', scrollY: -window.scrollY, windowHeight: el.scrollHeight });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * imgW) / canvas.width;

      if (imgH <= usableH) {
        pdf.addImage(imgData, 'PNG', margin, margin, imgW, imgH);
      } else {
        let yOffset = 0;
        let pageNum = 0;
        while (yOffset < imgH) {
          if (pageNum > 0) pdf.addPage();
          const srcY = (yOffset / imgH) * canvas.height;
          const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.ceil(srcH);
          const ctx = sliceCanvas.getContext('2d');
          ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
          const sliceData = sliceCanvas.toDataURL('image/png');
          const sliceH = (srcH * imgW) / canvas.width;
          pdf.addImage(sliceData, 'PNG', margin, margin, imgW, sliceH);
          yOffset += usableH;
          pageNum++;
        }
      }

      const name = mode === 'compare'
        ? `ASKI_Karsilastirma_${cmpP1Start}-${cmpP1End}_vs_${cmpP2Start}-${cmpP2End}.pdf`
        : `ASKI_Analiz_${startPeriod}-${endPeriod}.pdf`;
      pdf.save(name);
      setPdfExporting(false);
    } catch (e) { console.error('PDF olusturulamadi:', e); setPdfExporting(false); }
  };

  const chartData = data?.rows?.map(r => {
    if (data.groupBy === 'period') {
      return { name: fmtPeriod(r.donem), toplam: parseFloat(r.toplam_tuketim) || 0, ortalama: parseFloat(r.ort_tuketim) || 0, abone: parseInt(r.abone_sayisi) || 0 };
    }
    if (data.groupBy === 'ilce') {
      return { name: r.ilce_adi || 'Bilinmiyor', toplam: parseFloat(r.toplam_tuketim) || 0, ortalama: parseFloat(r.ort_tuketim) || 0, abone: parseInt(r.abone_sayisi) || 0 };
    }
    if (data.groupBy === 'abone') {
      return { name: (r.abone_adi || r.aboneno || '').substring(0, 25), toplam: parseFloat(r.toplam_tuketim) || 0, ortalama: parseFloat(r.ort_tuketim) || 0, donem: parseInt(r.donem_sayisi) || 0 };
    }
    return null;
  })?.filter(Boolean)?.slice(0, pdfExporting ? 500 : 50) || [];

  const cmpChartData = (() => {
    if (!cmpData) return [];
    const allPeriods = new Set();
    cmpData.period1?.rows?.forEach(r => allPeriods.add(r.donem));
    cmpData.period2?.rows?.forEach(r => allPeriods.add(r.donem));
    const sorted = [...allPeriods].sort();
    const p1Map = {};
    const p2Map = {};
    cmpData.period1?.rows?.forEach(r => { p1Map[r.donem] = parseFloat(r.toplam) || 0; });
    cmpData.period2?.rows?.forEach(r => { p2Map[r.donem] = parseFloat(r.toplam) || 0; });
    return sorted.map(d => ({ name: fmtPeriod(d), donem1: p1Map[d] || 0, donem2: p2Map[d] || 0 }));
  })();

  if (metaLoading) return <div className={s.loading}>Veriler hazirlaniyor...</div>;

  return (
    <div className={s.container}>
      {/* Filtreler */}
      <div className={s.filterPanel}>
        <div className={s.filterTitle}>Donemsel Analiz ve Karsilastirma</div>
        <div className={s.filterGrid}>
          <div className={s.filterGroup}>
            <label>Ilce</label>
            <select value={ilce} onChange={e => setIlce(e.target.value)}>
              <option value="">Tum Ilceler</option>
              {meta.ilceler.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className={s.filterGroup}>
            <label>Abone</label>
            <select value={aboneno} onChange={e => setAboneno(e.target.value)}>
              <option value="">Tum Aboneler</option>
              {meta.aboneler.map(a => <option key={a.aboneno} value={a.aboneno}>{a.abone_adi || a.aboneno}</option>)}
            </select>
          </div>
        </div>
        <div className={s.btnRow}>
          <button className={`${s.btn} ${mode === 'analysis' ? s.btnPrimary : s.btnOutline}`} onClick={() => setMode('analysis')}>Donem Analizi</button>
          <button className={`${s.btn} ${mode === 'compare' ? s.btnPrimary : s.btnOutline}`} onClick={() => setMode('compare')}>Donem Karsilastirma</button>
        </div>
      </div>

      <div ref={pdfRef} className={s.pdfArea}>
        {/* Analiz Modu */}
        {mode === 'analysis' && (
          <>
            <div className={s.filterPanel}>
              <div className={s.filterGrid}>
                <div className={s.filterGroup}>
                  <label>Baslangic Donemi</label>
                  <select value={startPeriod} onChange={e => setStartPeriod(e.target.value)}>
                    {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                  </select>
                </div>
                <div className={s.filterGroup}>
                  <label>Bitis Donemi</label>
                  <select value={endPeriod} onChange={e => setEndPeriod(e.target.value)}>
                    {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                  </select>
                </div>
                <div className={s.filterGroup}>
                  <label>Gruplama</label>
                  <select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                    <option value="period">Doneme Gore</option>
                    <option value="ilce">Ilceye Gore</option>
                    <option value="abone">Aboneye Gore</option>
                    <option value="raw">Ham Veri</option>
                  </select>
                </div>
              </div>
              <div className={s.btnRow}>
                <button className={`${s.btn} ${s.btnPrimary} ${loading ? s.btnDisabled : ''}`} onClick={runQuery}>Sorgula</button>
              </div>
            </div>

            {loading && <div className={s.loading}>Veriler yukleniyor...</div>}

            {data && !loading && (
              <>
                {data.groupBy !== 'raw' && (
                  <div className={s.statsRow}>
                    <div className={`${s.statCard} ${s.neutral}`}>
                      <strong>{data.count}</strong>
                      <span>{data.groupBy === 'period' ? 'Donem' : data.groupBy === 'ilce' ? 'Ilce' : 'Abone'}</span>
                    </div>
                    <div className={s.statCard}>
                      <strong>{fmtNum(data.rows.reduce((a, r) => a + parseFloat(r.toplam_tuketim || r.toplam || 0), 0))}</strong>
                      <span>Toplam Tuketim (m3)</span>
                    </div>
                    <div className={s.statCard}>
                      <strong>{fmtNum(data.rows.reduce((a, r) => a + parseFloat(r.ort_tuketim || r.ortalama || 0), 0) / (data.count || 1))}</strong>
                      <span>Ort. Tuketim (m3)</span>
                    </div>
                  </div>
                )}

                {chartData.length > 0 && data.groupBy !== 'raw' && (
                  <>
                    <div className={s.tabs}>
                      <button className={`${s.tab} ${s.active}`}>Grafik</button>
                    </div>
                    <div className={s.chartPanel}>
                      <div className={s.chartTypeRow}>
                        <button className={`${s.chartTypeBtn} ${chartType === 'bar' ? s.active : ''}`} onClick={() => setChartType('bar')}>Bar</button>
                        <button className={`${s.chartTypeBtn} ${chartType === 'line' ? s.active : ''}`} onClick={() => setChartType('line')}>Cizgi</button>
                      </div>
                      <ResponsiveContainer width="100%" height={350}>
                        {chartType === 'bar' ? (
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" fontSize={11} angle={-30} textAnchor="end" height={60} />
                            <YAxis fontSize={11} />
                            <Tooltip formatter={(v) => fmtNum(v)} />
                            <Legend />
                            <Bar dataKey="toplam" name="Toplam Tuketim (m3)" fill="#1e40af" radius={[4,4,0,0]} />
                            <Bar dataKey="ortalama" name="Ortalama (m3)" fill="#60a5fa" radius={[4,4,0,0]} />
                          </BarChart>
                        ) : (
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" fontSize={11} angle={-30} textAnchor="end" height={60} />
                            <YAxis fontSize={11} />
                            <Tooltip formatter={(v) => fmtNum(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="toplam" name="Toplam Tuketim (m3)" stroke="#1e40af" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="ortalama" name="Ortalama (m3)" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                <div className={s.tableWrap}>
                  <table className={s.dataTable}>
                    <thead>
                      <tr>
                        {data.groupBy === 'period' && <><th>Donem</th><th>Abone Sayisi</th><th>Toplam (m3)</th><th>Ortalama (m3)</th><th>Min (m3)</th><th>Max (m3)</th></>}
                        {data.groupBy === 'ilce' && <><th>Ilce</th><th>Abone Sayisi</th><th>Toplam (m3)</th><th>Ortalama (m3)</th></>}
                        {data.groupBy === 'abone' && <><th>Abone No</th><th>Park Adi</th><th>Ilce</th><th>Toplam (m3)</th><th>Ort. (m3)</th><th>Donem</th></>}
                        {data.groupBy === 'raw' && <><th>Donem</th><th>Abone</th><th>Ilce</th><th>Mahalle</th><th>Tuketim (m3)</th><th>Kaynak</th></>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.slice(0, pdfExporting ? data.rows.length : 200).map((r, i) => (
                        <tr key={i}>
                          {data.groupBy === 'period' && <><td>{fmtPeriod(r.donem)}</td><td>{r.abone_sayisi}</td><td>{fmtNum(r.toplam_tuketim)}</td><td>{fmtNum(r.ort_tuketim)}</td><td>{fmtNum(r.min_tuketim)}</td><td>{fmtNum(r.max_tuketim)}</td></>}
                          {data.groupBy === 'ilce' && <><td>{r.ilce_adi}</td><td>{r.abone_sayisi}</td><td>{fmtNum(r.toplam_tuketim)}</td><td>{fmtNum(r.ort_tuketim)}</td></>}
                          {data.groupBy === 'abone' && <><td>{r.aboneno}</td><td>{r.abone_adi}</td><td>{r.ilce_adi}</td><td>{fmtNum(r.toplam_tuketim)}</td><td>{fmtNum(r.ort_tuketim)}</td><td>{r.donem_sayisi}</td></>}
                          {data.groupBy === 'raw' && <><td>{fmtPeriod(r.donem)}</td><td>{r.abone_adi}</td><td>{r.ilce_adi}</td><td>{r.mahalle_adi}</td><td>{fmtNum(r.m3_tuketim)}</td><td>{r.kaynak}</td></>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!pdfExporting && data.rows.length > 200 && (
                    <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: '#64748b', borderTop: '1px solid #e2e8f0' }}>
                      Ekranda ilk 200 satir gosteriliyor (toplam: {data.rows.length}). PDF ciktisinda tum satirlar yer alir.
                    </div>
                  )}
                </div>
              </>
            )}

            {!data && !loading && <div className={s.empty}>Donem secip "Sorgula" butonuna basin.</div>}
          </>
        )}

        {/* Karsilastirma Modu */}
        {mode === 'compare' && (
          <>
            <div className={s.filterPanel}>
              <div className={s.compareGrid}>
                <div>
                  <div className={s.filterTitle} style={{ color: '#1e40af' }}>Donem 1</div>
                  <div className={s.filterGrid}>
                    <div className={s.filterGroup}>
                      <label>Baslangic</label>
                      <select value={cmpP1Start} onChange={e => setCmpP1Start(e.target.value)}>
                        {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                      </select>
                    </div>
                    <div className={s.filterGroup}>
                      <label>Bitis</label>
                      <select value={cmpP1End} onChange={e => setCmpP1End(e.target.value)}>
                        {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={s.filterTitle} style={{ color: '#16a34a' }}>Donem 2</div>
                  <div className={s.filterGrid}>
                    <div className={s.filterGroup}>
                      <label>Baslangic</label>
                      <select value={cmpP2Start} onChange={e => setCmpP2Start(e.target.value)}>
                        {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                      </select>
                    </div>
                    <div className={s.filterGroup}>
                      <label>Bitis</label>
                      <select value={cmpP2End} onChange={e => setCmpP2End(e.target.value)}>
                        {meta.periods.map(p => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className={s.btnRow}>
                <button className={`${s.btn} ${s.btnPrimary} ${cmpLoading ? s.btnDisabled : ''}`} onClick={runCompare}>Karsilastir</button>
              </div>
            </div>

            {cmpLoading && <div className={s.loading}>Karsilastirma yukleniyor...</div>}

            {cmpData && !cmpLoading && (
              <>
                <div className={s.statsRow}>
                  <div className={`${s.statCard} ${s.neutral}`}>
                    <strong>{fmtNum(cmpData.period1?.total)}</strong>
                    <span>Donem 1 Toplam (m3)</span>
                  </div>
                  <div className={`${s.statCard} ${s.neutral}`}>
                    <strong>{fmtNum(cmpData.period2?.total)}</strong>
                    <span>Donem 2 Toplam (m3)</span>
                  </div>
                  <div className={`${s.statCard} ${parseFloat(cmpData.change) > 0 ? s.up : parseFloat(cmpData.change) < 0 ? s.down : s.neutral}`}>
                    <strong>{cmpData.change !== null ? `%${cmpData.change}` : '-'}</strong>
                    <span>Degisim</span>
                    {cmpData.change !== null && (
                      <div className={`${s.changeTag} ${parseFloat(cmpData.change) > 0 ? s.changeUp : s.changeDown}`}>
                        {parseFloat(cmpData.change) > 0 ? 'Artis' : 'Azalis'}
                      </div>
                    )}
                  </div>
                </div>

                {cmpChartData.length > 0 && (
                  <>
                    <div className={s.tabs}>
                      <button className={`${s.tab} ${s.active}`}>Karsilastirma Grafigi</button>
                    </div>
                    <div className={s.chartPanel}>
                      <div className={s.chartTypeRow}>
                        <button className={`${s.chartTypeBtn} ${chartType === 'bar' ? s.active : ''}`} onClick={() => setChartType('bar')}>Bar</button>
                        <button className={`${s.chartTypeBtn} ${chartType === 'line' ? s.active : ''}`} onClick={() => setChartType('line')}>Cizgi</button>
                      </div>
                      <ResponsiveContainer width="100%" height={350}>
                        {chartType === 'bar' ? (
                          <BarChart data={cmpChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" fontSize={11} angle={-30} textAnchor="end" height={60} />
                            <YAxis fontSize={11} />
                            <Tooltip formatter={(v) => fmtNum(v)} />
                            <Legend />
                            <Bar dataKey="donem1" name={`D1: ${fmtPeriod(cmpP1Start)}-${fmtPeriod(cmpP1End)}`} fill="#1e40af" radius={[4,4,0,0]} />
                            <Bar dataKey="donem2" name={`D2: ${fmtPeriod(cmpP2Start)}-${fmtPeriod(cmpP2End)}`} fill="#16a34a" radius={[4,4,0,0]} />
                          </BarChart>
                        ) : (
                          <LineChart data={cmpChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" fontSize={11} angle={-30} textAnchor="end" height={60} />
                            <YAxis fontSize={11} />
                            <Tooltip formatter={(v) => fmtNum(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="donem1" name={`D1: ${fmtPeriod(cmpP1Start)}-${fmtPeriod(cmpP1End)}`} stroke="#1e40af" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="donem2" name={`D2: ${fmtPeriod(cmpP2Start)}-${fmtPeriod(cmpP2End)}`} stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                <div className={s.compareGrid}>
                  <div>
                    <div className={s.compareLabel}>Donem 1 Detay</div>
                    <div className={s.tableWrap} style={{ maxHeight: 250 }}>
                      <table className={s.dataTable}>
                        <thead><tr><th>Donem</th><th>Toplam (m3)</th><th>Abone</th><th>Ort. (m3)</th></tr></thead>
                        <tbody>
                          {cmpData.period1?.rows?.map((r, i) => (
                            <tr key={i}><td>{fmtPeriod(r.donem)}</td><td>{fmtNum(r.toplam)}</td><td>{r.abone_sayisi}</td><td>{fmtNum(r.ortalama)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <div className={s.compareLabel}>Donem 2 Detay</div>
                    <div className={s.tableWrap} style={{ maxHeight: 250 }}>
                      <table className={s.dataTable}>
                        <thead><tr><th>Donem</th><th>Toplam (m3)</th><th>Abone</th><th>Ort. (m3)</th></tr></thead>
                        <tbody>
                          {cmpData.period2?.rows?.map((r, i) => (
                            <tr key={i}><td>{fmtPeriod(r.donem)}</td><td>{fmtNum(r.toplam)}</td><td>{r.abone_sayisi}</td><td>{fmtNum(r.ortalama)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!cmpData && !cmpLoading && <div className={s.empty}>Iki donem secip "Karsilastir" butonuna basin.</div>}
          </>
        )}
      </div>

      {/* PDF Export - her zaman gorunur */}
      {(data || cmpData) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 0' }}>
          <button className={`${s.btn} ${s.btnDanger}`} onClick={exportPDF} disabled={pdfExporting}>
            {pdfExporting ? 'PDF Hazirlaniyor...' : 'PDF Indir'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AskiAnalysis;
